/**
 * CodableWorkspace — split-panel view pro Codable projekt.
 * Levá strana: preview chrome (device toggle + path bar + iframe s preview URL).
 * Pravá strana: embedded chat pane s AI asistentem.
 * Nahoře: název + Publish tlačítko + odkaz na GitLab.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Code2,
  ExternalLink,
  GitBranch,
  Globe,
  Info,
  Loader2,
  Monitor,
  MousePointerClick,
  PanelRightClose,
  PanelRightOpen,
  Plug,
  RefreshCw,
  Settings,
  Shield,
  Smartphone,
  Tablet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import McpConnectModal from '../../../components/McpConnectModal';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { themedConfirm, themedPrompt } from '../../../lib/themed-dialogs';
import { operationPollInterval, projectReadinessPollInterval } from '../../../lib/bounded-polling';
import { clearUiContext, setUiContext } from '../../../lib/ui-context';
import { useCodableInspect } from '../../../lib/useCodableInspect';
import { visibleProjectId } from '../../../lib/project-id';
import { pickLiveOperation } from '../../../lib/lifecycle-operation';
import { refreshCodableWorkspace } from '../../../lib/codable-workspace-refresh';
import { CODABLE_CHAT_OPEN } from '../../../lib/codable-chat-ask';
import {
  AsyncStatusToggle,
  BackButton,
  Badge,
  Button,
  Input,
  LoadingStatus,
  Modal,
  PublishPanel,
  StatusDot,
} from '../../../components/ui';
import WorkspaceWorkPanel from '../../../components/WorkspaceWorkPanel';
import ReleaseCarousel from '../../../components/ReleaseCarousel';
import CodableChatPane from './CodableChatPane';
import ConnectIdeModal from './ConnectIdeModal';
import CodePanel from './CodePanel';
import ActivityModal from './ActivityModal';
import HistoryModal from './HistoryModal';
import KnowledgeModal from './KnowledgeModal';
import ProjectSettingsModal from './ProjectSettingsModal';
import ProjectLlmSettingsModal from './ProjectLlmSettingsModal';
import ProjectLogModal from './ProjectLogModal';

interface CodableProject {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  tenant_id: string | null;
  gitlab_project_key: string | null;
  env_name: string | null;
  frontend_domain: string | null;
  frontend_public_enabled: boolean;
  frontend_public: boolean;
  chat_profile: string | null;
  last_commit_sha?: string | null;
  updated_at?: string | null;
  source?: 'native' | 'lovable' | 'acm';
  origin?: {
    node?: string;
    endpoint?: string;
    mode?: 'clone' | 'mirror';
    stale?: boolean;
  } | null;
}

interface GetResp {
  ok: boolean;
  project?: CodableProject;
  orchestrator_project_id?: string | null;
  project_binding?: {
    project_id: string;
    production_service: string | null;
    workspace_service: string | null;
  } | null;
  env_status?: string;
  readiness?: {
    ready: boolean;
    workspace_ready: boolean;
    preview_ready: boolean;
    phase: 'ready' | 'orchestrator' | 'services' | 'runtime' | 'publication' | 'error';
    detail: string;
    checks: Array<{
      key: string;
      ok: boolean;
      status: string;
      detail: string;
    }>;
    errors: string[];
  };
  services?: Record<string, unknown>;
  gitlab_web_url?: string | null;
  gitlab_path?: string | null;
  preview_url?: string | null;
  public_url?: string | null;
  development?: {
    ui_url?: string | null;
    ui_desired?: boolean;
    ui_actual?: boolean;
    mcp_url?: string | null;
    oauth_enabled?: boolean;
    oauth_platform_enabled?: boolean;
    mcp?: { enabled?: boolean; url?: string | null };
  } | null;
  production?: { ui_url?: string | null; preview_url?: string | null } | null;
  sso?: { mode?: 'off' | 'platform' | 'custom'; issuer?: string | null; client_id?: string | null } | null;
  hub_app_key_enabled?: boolean;
  mcp_connection?: { url?: string | null; kind?: string; enabled?: boolean } | null;
  publish?: {
    domain: string | null;
    desired: boolean;
    actual: boolean;
    url: string | null;
    is_internal_domain?: boolean;
    is_bare_apex_without_alt?: boolean;
    holding?: boolean;
    holding_mode?: 'preparing' | 'updating' | null;
    holding_source?: 'universal' | 'custom' | null;
  };
  backend?: { type: string; enabled: boolean };
  fe_split?: boolean;
  workspace_instance?: string;
  prod_instance?: string;
  topology_target?: TopologyTarget | null;
  topology?: ProjectTopology | null;
  last_publish_event?: ProjectLogCall | null;
  project_status?: ProjectStatus | null;
  error?: string;
}

interface LifecycleOperation {
  id: string;
  slug: string;
  created_at?: string | null;
  project_id?: string | null;
  operation_type: 'sync' | 'promote' | 'delete' | 'topology-set' | 'build';
  status: 'queued' | 'running' | 'completed' | 'failed';
  phase: string;
  progress: number;
  error?: string | null;
  result?: Record<string, unknown> | null;
}

interface LifecycleStartResp {
  ok: boolean;
  operation_id?: string;
  status?: string;
  error?: string;
  sha?: string | null;
  already_current?: boolean;
}

type TopologyTarget = 'beta' | 'simple' | 'normal' | 'full_split';

interface ProjectTopology {
  mode?: TopologyTarget | string | null;
  target?: TopologyTarget | string | null;
  actual?: TopologyTarget | 'none' | string | null;
  label?: string | null;
  frontend_split?: boolean | null;
  backend_split?: boolean | null;
  verified?: boolean | null;
  drift?: boolean | null;
  issues?: string[];
  dev_domain?: string | null;
  prod_domain?: string | null;
}

interface ProjectLogCall {
  tool_name: string;
  raw_tool_name?: string | null;
  actor_entity_id?: string | null;
  actor_key_preview?: string | null;
  result_summary?: Record<string, unknown> | null;
  ok: boolean;
  error?: string | null;
  created_at: string;
}

interface ProjectStatus {
  checked_at: string;
  /** Karusel verzí prod frontendu: ostrá (live), náhled (preview), rezerva (reserved). */
  carousel?: {
    live?: string | null;
    preview?: string | null;
    reserved?: string | null;
    preview_is_live?: boolean;
    next?: string;
  } | null;
  topology?: {
    split: boolean;
    workspace_instance: string;
    prod_instance: string;
  };
  dev?: {
    head?: string | null;
    short_head?: string | null;
    remote_head?: string | null;
    remote_ref?: string | null;
    dirty?: boolean;
    changed_files?: number;
    ahead?: number | null;
    behind?: number | null;
  };
  prod?: {
    head?: string | null;
    short_head?: string | null;
    remote_head?: string | null;
    remote_ref?: string | null;
    dirty?: boolean;
    changed_files?: number;
    ahead?: number | null;
    behind?: number | null;
  };
  relation?: {
    same_instance?: boolean;
    same_head?: boolean | null;
    states?: string[];
    warnings?: string[];
  };
}

type DeviceMode = 'desktop' | 'tablet' | 'mobile';

type SecuritySeverity = 'high' | 'med' | 'low';
type SecurityFilter = 'all' | SecuritySeverity;

function topologyLabel(mode?: string | null): string {
  if (mode === 'beta') return 'Beta';
  if (mode === 'simple') return 'Simple';
  if (mode === 'normal') return 'Normal';
  if (mode === 'full_split') return 'Full Split';
  if (mode === 'none') return i18n.t('codable.wsNotFilled');
  return mode || i18n.t('codable.wsUnverified');
}

interface SecuritySummary {
  high: number;
  med: number;
  low: number;
  total: number;
  files?: number;
  targets?: string[];
}

interface ProjectSecurityFinding {
  file: string;
  line: number;
  message: string;
  severity: SecuritySeverity | string;
  category: string;
  content: string;
  target?: string;
  rule_id?: string;
}

interface SecurityResultsResp {
  error?: string;
  warning?: string;
  scan_incomplete?: boolean;
  ignored_incomplete_latest?: boolean;
  scan_id?: string;
  started_at?: string;
  finished_at?: string | null;
  status?: string;
  summary?: unknown;
  target_in_scan?: boolean;
  filtered_summary?: SecuritySummary;
  target_prefix?: string;
  findings?: ProjectSecurityFinding[];
}

const DEVICE_MODE_DEFS: {
  mode: DeviceMode;
  icon: typeof Monitor;
  labelKey: string;
}[] = [
  { mode: 'desktop', icon: Monitor, labelKey: 'codable.wsDesktop' },
  { mode: 'tablet', icon: Tablet, labelKey: 'codable.wsTablet' },
  { mode: 'mobile', icon: Smartphone, labelKey: 'codable.wsMobile' },
];

const DEVICE_WIDTH: Record<DeviceMode, string> = {
  desktop: 'max-w-full',
  tablet: 'max-w-[768px] border-x border-gray-800 shadow-2xl',
  mobile: 'max-w-[390px] border-x border-gray-800 shadow-2xl',
};

function fmtDateTime(value?: string | null): string {
  if (!value) return i18n.t('codable.wsUnknown');
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function summaryString(
  summary: Record<string, unknown> | null | undefined,
  keys: string[],
): string | null {
  if (!summary) return null;
  for (const key of keys) {
    const value = summary[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

function sameGitRef(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const aa = a.trim();
  const bb = b.trim();
  if (!aa || !bb) return false;
  return aa === bb || aa.startsWith(bb) || bb.startsWith(aa);
}

export default function CodableWorkspace() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [iframeKey, setIframeKey] = useState(0);
  const [publishOpen, setPublishOpen] = useState(false);
  const [connectIdeOpen, setConnectIdeOpen] = useState(false);
  const [llmModelsOpen, setLlmModelsOpen] = useState(false);
  // Lovable-parity: code mode (read-only file browser) + historie + knowledge
  const [view, setView] = useState<'preview' | 'code'>('preview');
  const [activityOpen, setActivityOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeTab, setKnowledgeTab] = useState<'instructions' | 'sources'>('instructions');
  const [projectLogOpen, setProjectLogOpen] = useState(false);
  // ?settings=1 (odkaz z Platform Hubu → Propojení projektu) otevře rovnou nastavení.
  const [settingsOpen, setSettingsOpen] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('settings'),
  );
  const [fromSettings, setFromSettings] = useState(false);
  const openFromSettings = (open: () => void) => {
    setFromSettings(true);
    setSettingsOpen(false);
    open();
  };
  const backToSettings = () => {
    setFromSettings(false);
    setSettingsOpen(true);
  };
  const [securityOpen, setSecurityOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [activeLifecycleId, setActiveLifecycleId] = useState<string | null>(null);
  const [handledLifecycleId, setHandledLifecycleId] = useState<string | null>(null);
  // Zásuvka svítí žlutě, když má user aspoň jeden aktivní token (sdílený
  // query key s McpConnectModal → create/revoke to hned překreslí)
  const { data: ideConnData } = useQuery({
    queryKey: ['acm-connect-tokens'],
    queryFn: () =>
      api.mcpSession<{ ok: boolean; tokens?: { enabled: boolean }[] }>(
        'acm-self.connect',
        {
          action: 'list',
        },
      ),
    staleTime: 60_000,
    retry: false,
  });
  const ideConnected = (ideConnData?.tokens ?? []).some((tk) => tk.enabled);
  const [topologyInfoOpen, setTopologyInfoOpen] = useState(false);
  const [topologyDraft, setTopologyDraft] = useState<TopologyTarget>('beta');
  const [topologyProdDomain, setTopologyProdDomain] = useState('');
  const [topologyBusy, setTopologyBusy] = useState(false);
  /** Které varianty má tenant schválené — zamčené se ukazují, ale nejdou zvolit. */
  const topologyOptionsQ = useQuery({
    queryKey: ['topology-options'],
    enabled: topologyInfoOpen,
    queryFn: () =>
      api.mcp<{ ok: boolean; allowed?: TopologyTarget[] }>(
        'orchestrator.topology_options',
        {},
      ),
  });
  const approvedTargets: TopologyTarget[] = topologyOptionsQ.data?.ok
    ? topologyOptionsQ.data.allowed ?? ['beta', 'simple', 'normal', 'full_split']
    : ['beta', 'simple', 'normal', 'full_split'];
  const {
    iframeRef,
    inspect,
    selection,
    toggleInspect,
    clearSelection,
    onIframeLoad,
  } = useCodableInspect(iframeKey);
  const [device, setDevice] = useState<DeviceMode>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const v = window.localStorage.getItem('acm_codable_device');
    return v === 'tablet' || v === 'mobile' ? v : 'desktop';
  });
  const switchDevice = (d: DeviceMode) => {
    setDevice(d);
    try {
      window.localStorage.setItem('acm_codable_device', d);
    } catch { /* ignore */ }
  };
  // Path bar — path je jen client-side navigace uvnitř preview originu.
  const [path, setPath] = useState('/');
  const [pathInput, setPathInput] = useState('/');
  const commitPath = () => {
    let v = pathInput.trim();
    if (!v.startsWith('/')) v = `/${v}`;
    setPath(v);
    setPathInput(v);
  };
  const [chatOpen, setChatOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const v = window.localStorage.getItem('acm_codable_chat_open');
    return v === null ? true : v === '1';
  });
  const toggleChat = () => {
    setChatOpen((c) => {
      const next = !c;
      try {
        window.localStorage.setItem('acm_codable_chat_open', next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  };

  const [lastGitSync, setLastGitSync] = useState<{ text: string; ok: boolean } | null>(null);
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['codable-project', id],
    queryFn: () => api.mcp<GetResp>('codable.project_get', { id }),
    enabled: !!id,
    // Stabilní workspace se nepolluje. Provisioning má konečný backoff;
    // po limitu zůstává ruční „Ověřit znovu“, takže opuštěný tab negeneruje
    // nekonečné MCP/audit záznamy.
    refetchInterval: (query) =>
      projectReadinessPollInterval(
        query.state.data?.readiness,
        query.state.dataUpdateCount,
      ),
  });
  const lifecycleListQ = useQuery({
    queryKey: ['orchestrator-project-operations', 'mine'],
    queryFn: () =>
      api.mcp<{ ok: boolean; operations?: LifecycleOperation[] }>(
        'orchestrator.project_operation_list',
        { mine: true, limit: 30 },
      ),
    enabled: !!data?.orchestrator_project_id,
    staleTime: 5_000,
  });
  useEffect(() => {
    if (activeLifecycleId || !data?.orchestrator_project_id) return;
    const pending = pickLiveOperation(lifecycleListQ.data?.operations, {
      matches: (operation) =>
        operation.project_id === data.orchestrator_project_id &&
        ['sync', 'promote', 'topology-set', 'build'].includes(operation.operation_type),
      live: ['queued', 'running'],
    });
    if (pending) setActiveLifecycleId(pending.id);
  }, [activeLifecycleId, data?.orchestrator_project_id, lifecycleListQ.data?.operations]);
  const lifecycleQ = useQuery({
    queryKey: ['orchestrator-project-operation', activeLifecycleId],
    queryFn: () =>
      api.mcp<{ ok: boolean; operation?: LifecycleOperation; error?: string }>(
        'orchestrator.project_operation_get',
        { operation_id: activeLifecycleId },
      ),
    enabled: !!activeLifecycleId,
    refetchInterval: (query) =>
      operationPollInterval(
        query.state.data?.operation?.status,
        query.state.dataUpdateCount,
      ),
  });
  const lifecycleOperation = lifecycleQ.data?.operation;
  const lifecycleRunning = lifecycleOperation?.status === 'queued' ||
    lifecycleOperation?.status === 'running';
  useEffect(() => {
    if (!lifecycleOperation || handledLifecycleId === lifecycleOperation.id) return;
    if (lifecycleOperation.status === 'completed') {
      const finishedId = lifecycleOperation.id;
      setHandledLifecycleId(finishedId);
      toast.success(
        lifecycleOperation.operation_type === 'promote'
          ? t('codable.promoteDone', 'Publikováno na PROD')
          : lifecycleOperation.operation_type === 'topology-set'
          ? t('codable.topologyDone', 'Varianta projektu je nastavená')
          : lifecycleOperation.operation_type === 'build'
          ? t('codable.buildDone', 'Build hotový — artefakt je připravený k publikaci')
          : t('codable.deployDone', 'Git sync hotový — obnovuji náhled'),
      );
      // Build nemění náhled ani PROD — iframe se neobnovuje.
      if (lifecycleOperation.operation_type !== 'build') setIframeKey((key) => key + 1);
      refreshCodableWorkspace(qc, { slug: data?.project?.slug });
      const hide = window.setTimeout(() => {
        refreshCodableWorkspace(qc, { slug: data?.project?.slug });
        setActiveLifecycleId((cur) => (cur === finishedId ? null : cur));
      }, 1600);
      return () => window.clearTimeout(hide);
    } else if (lifecycleOperation.status === 'failed') {
      setHandledLifecycleId(lifecycleOperation.id);
      toast.error(
        lifecycleOperation.error ?? t('codable.lifecycleFailed', 'Projektová operace selhala'),
      );
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
    }
  }, [handledLifecycleId, lifecycleOperation, qc, t, data?.project?.slug]);
  const lifecycleRetryMut = useMutation({
    mutationFn: () =>
      api.mcp<LifecycleStartResp>('orchestrator.project_operation_retry', {
        operation_id: activeLifecycleId,
      }),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? t('codable.lifecycleRetryFailed', 'Opakování operace selhalo'));
        return;
      }
      setHandledLifecycleId(null);
      void lifecycleQ.refetch();
    },
    onError: (error: unknown) => toast.error(String((error as Error).message ?? error)),
  });
  // Git sync (rebuild z HEAD repa) — viditelně v liště. Chat commituje sám,
  // tohle je pro pushe zvenčí (IDE / externí agent). Po úspěchu refresh iframe.
  const deployMut = useMutation({
    // Refresh/rebuild = WORKSPACE (DEV) instance; PROD se nasazuje promotem.
    mutationFn: () => {
      if (!data?.orchestrator_project_id) {
        throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      }
      return api.mcp<LifecycleStartResp>(
        `${data.orchestrator_project_id}.orchestrator.sync`,
        {},
      );
    },
    onSuccess: (r) => {
      if (r.ok && r.operation_id && r.status && r.status !== 'completed') {
        setHandledLifecycleId(null);
        setActiveLifecycleId(r.operation_id);
        return;
      }
      if (r.ok) {
        const sha = r.sha || '—';
        const msg = r.already_current
          ? t('codable.deployUpToDate', 'Git sync: už aktuální ({{sha}})', { sha })
          : t('codable.deployPulled', 'Git sync: staženo → {{sha}}', { sha });
        toast.success(msg, { duration: 5000 });
        setLastGitSync({ text: msg, ok: true });
        if (!r.already_current) setIframeKey((key) => key + 1);
        refreshCodableWorkspace(qc, {
          slug: data?.project?.slug,
          preview: !r.already_current,
        });
        return;
      }
      const fail = `${t('codable.deploy', 'Git sync')}: ${r.error ?? t('codable.errorShort', 'chyba')}`;
      setLastGitSync({ text: fail, ok: false });
      toast.error(fail);
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
    },
    onError: (e: unknown) => {
      const fail = String((e as Error).message ?? e);
      setLastGitSync({ text: fail, ok: false });
      toast.error(fail);
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
    },
  });
  const previewRebuilding = Boolean(
    deployMut.isPending ||
      (lifecycleRunning &&
        (lifecycleOperation?.operation_type === 'sync' ||
          lifecycleOperation?.operation_type === 'promote')),
  );

  const holdingMut = useMutation({
    mutationFn: (next: {
      enabled: boolean;
      mode?: 'preparing' | 'updating';
      source?: 'universal' | 'custom';
    }) =>
      api.mcp<{ ok: boolean; error?: string; warning?: string }>(
        'codable.public_holding',
        { id, ...next },
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(
          r.error ?? t('codable.holdingFail', 'Construction page se nepodařilo přepnout'),
        );
        return;
      }
      if (r.warning) toast.message(r.warning);
      toast.success(
        r.warning
          ? t('codable.holdingOnSameHost', 'Construction page je zapnutá (stejná adresa jako DEV).')
          : t('codable.holdingChanged', 'Construction page na veřejné URL je nastavená.'),
      );
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const promoteMut = useMutation({
    // Nasazení na PROD FE — jen commitnutý stav (HEAD), DEV workspace nesahá.
    mutationFn: () => {
      if (!data?.orchestrator_project_id) {
        throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      }
      return api.mcp<LifecycleStartResp>(
        `${data.orchestrator_project_id}.orchestrator.promote`,
        { confirm: true },
      );
    },
    onSuccess: (r) => {
      if (r.ok && r.operation_id) {
        setHandledLifecycleId(null);
        setActiveLifecycleId(r.operation_id);
      } else {
        toast.error(
          `${t('codable.promote', 'Publikovat')}: ${r.error ?? t('codable.errorShort', 'chyba')}`,
        );
        refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
      }
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const enableBackendMut = useMutation({
    mutationFn: () => {
      if (!data?.orchestrator_project_id) {
        throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      }
      return api.mcp<{ ok: boolean; error?: string; apply_status?: string }>(
        `${data.orchestrator_project_id}.orchestrator.enable-backend`,
        { backend: 'supabase' },
      );
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.enableBackendFail', 'Chyba zapnutí backendu'));
        return;
      }
      toast.success(
        t('codable.backendOn', 'Backend zapnut ({{status}})', { status: r.apply_status }),
      );
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  /** Změna Beta/Simple/Normal/Full Split přes orchestrátor (topology-set). */
  const applyTopologyChange = async (opts: { apply: boolean }) => {
    const prj = data?.orchestrator_project_id;
    if (!prj) {
      toast.error(
        t(
          'codable.wsTopologyNoOrch',
          'Project is not bound to orchestrator — topology cannot be changed here.',
        ),
      );
      return;
    }
    if (topologyDraft !== 'beta' && !topologyProdDomain.trim()) {
      toast.error(
        t(
          'codable.wsTopologyProdRequired',
          'PROD domain is required for Simple / Normal / Full Split.',
        ),
      );
      return;
    }
    // Ptát se znovu na to, co člověk právě vybral a odklikl, nemá smysl —
    // stavba variant nic neruší. Potvrzení patří jen tam, kde se něco odebírá:
    // Beta nemá ostrou adresu, takže publikovaná PROD doména zmizí z internetu.
    const losesProdDomain = opts.apply && topologyDraft === 'beta' &&
      !!data?.topology?.prod_domain;
    if (losesProdDomain) {
      const ok = await themedConfirm(
        {
          title: t('codable.wsTopologyBetaConfirmTitle', 'Vypnout veřejnou adresu?'),
          description: t(
            'codable.wsTopologyBetaConfirmDesc',
            'Beta běží jen jako náhled. {{domain}} přestane být veřejně dostupná, ' +
              'data a běžící služby zůstanou.',
            { domain: data?.topology?.prod_domain ?? '' },
          ),
          confirmLabel: t('codable.wsTopologyBetaConfirmBtn', 'Přepnout na Beta'),
          danger: true,
        },
      );
      if (!ok) return;
    }
    setTopologyBusy(true);
    try {
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        drift?: boolean;
        operation_id?: string;
      }>(
        `${prj}.orchestrator.topology-set`,
        {
          target: topologyDraft,
          apply: opts.apply,
          confirm: true,
          ...(topologyDraft !== 'beta' && topologyProdDomain.trim()
            ? { prod_domain: topologyProdDomain.trim() }
            : {}),
        },
      );
      if (!r.ok) {
        toast.error(r.error ?? t('codable.errorGeneric', { msg: 'topology-set' }));
        return;
      }
      if (r.operation_id) {
        // Naplnění běží frontou (minuty): průběh kreslí lišta projektu a po
        // dokončení se workspace obnoví sám — dřív se čekalo na jedno volání,
        // které prohlížeč po 120 s zrušil, a stav zůstal starý.
        setHandledLifecycleId(null);
        setActiveLifecycleId(r.operation_id);
        toast.success(
          t('codable.wsTopologyQueued', 'Varianta se staví — průběh je v liště projektu'),
        );
        setTopologyInfoOpen(false);
        return;
      }
      toast.success(
        opts.apply
          ? t('codable.wsTopologyApplied', 'Topology saved and applied')
          : t('codable.wsTopologySaved', 'Topology choice saved'),
      );
      refreshCodableWorkspace(qc, { slug: data?.project?.slug, preview: false });
      setTopologyInfoOpen(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setTopologyBusy(false);
    }
  };

  // UI context registr — jaký projekt/záložka je otevřená (agent/IDE to čte).
  // POZOR: hooky MUSÍ být před early returny (React #310) — proto data?.project.
  const projSlug = data?.project?.slug;
  useEffect(() => {
    if (!projSlug) return;
    setUiContext({ page: 'codable-workspace', project: projSlug, tab: view });
    return () => clearUiContext(['project', 'open_file', 'open_file_dirty', 'selection']);
  }, [projSlug, view]);

  // History restore / agent tools → hard reload preview iframe (static dist).
  // MUSÍ být před early return (isLoading / !project) — jinak React #310
  // (Rendered more hooks than during the previous render).
  useEffect(() => {
    const onRefresh = (e: Event) => {
      const detail = (e as CustomEvent<{ slug?: string }>).detail;
      if (detail?.slug && projSlug && detail.slug !== projSlug) return;
      setIframeKey((k) => k + 1);
    };
    window.addEventListener('codable:preview-refresh', onRefresh);
    return () => window.removeEventListener('codable:preview-refresh', onRefresh);
  }, [projSlug]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const s = (e as CustomEvent<{ slug?: string }>).detail?.slug;
      if (s && projSlug && s !== projSlug) return;
      setChatOpen(true);
      try {
        window.localStorage.setItem('acm_codable_chat_open', '1');
      } catch { /* ignore */ }
    };
    window.addEventListener(CODABLE_CHAT_OPEN, onOpen);
    return () => window.removeEventListener(CODABLE_CHAT_OPEN, onOpen);
  }, [projSlug]);

  // Delete zůstává v listu přes projektově kvalifikovaný orchestrator.delete —
  // ne ve workspace toolbaru, aby se dalo omylem smazat běžící projekt.
  // Filip 2026-07-07: "projekt by neměl jít smazat z úprav". Smazání se řeší
  // z CodableList (grid s karta actions).

  if (isLoading) {
    return (
      <LoadingStatus
        variant='page'
        title={t('codable.loadingProjects')}
        status={t('codable.loadingProjectsHint')}
        steps={[
          { id: 'git', label: t('codable.git'), state: 'active' },
          { id: 'preview', label: t('codable.wsPreview'), state: 'pending' },
          { id: 'pub', label: t('codable.wsPublication'), state: 'pending' },
        ]}
      />
    );
  }
  const p = data?.project;
  if (!p) {
    return (
      <div className='p-6'>
        <div className='text-red-400 mb-4'>
          Projekt nenalezen: {data?.error ?? id}
        </div>
        <Link to='/modules/codable'>
          <Button variant='secondary' icon={<ArrowLeft size={15} />}>
            {t('common.back')}
          </Button>
        </Link>
      </div>
    );
  }

  // Preview URL — SSOT je catalog network/{domain}, response.preview_url je resolved
  // (fallback na frontend_domain z codable_projects pokud catalog entry ještě chybí)
  const previewUrl = data?.preview_url ??
    (p.frontend_domain ? `https://${p.frontend_domain}` : '');
  const publishActual = data?.publish?.actual ?? p.frontend_public;
  // `publish.desired` posílá tool VŽDY (boolean, ne volitelné) — fallback na
  // `frontend_public_enabled` byl třetí místo, kde by přepínač ukázal příznak
  // z DB místo stavu trasy. Přesně tak lhal MCP přepínač u biopharm-helpdesk
  // (2026-08-30). Když by hodnota chyběla, je správná odpověď „vypnuto", ne
  // „vezmi si tu druhou pravdu".
  const publishDesired = data?.publish?.desired === true;
  const publishSyncing = publishDesired !== publishActual;
  const splitPublished = data?.fe_split === true && publishActual;
  const status = data?.env_status ?? 'unknown';
  // Orch env_status vocab (modules/orchestrator/orch-utils.ts EnvStatus):
  //   pending | applied | partial | error | destroying | destroyed
  // + apphost service status per service. Ready = env je 'applied' (spec projede
  // apply bez chyby); 'ok' zůstává jako historický synonym pro back-compat.
  const READY = new Set(['ok', 'applied', 'running']);
  const PROVISIONING = new Set([
    'pending',
    'partial',
    'destroying',
    'provisioning',
  ]);
  const readiness = data?.readiness;
  const isReady = readiness?.ready ?? READY.has(status);
  const readinessFailed = readiness?.phase === 'error';
  const isProvisioning = readiness
    ? !readiness.ready && !readinessFailed
    : PROVISIONING.has(status);

  const previewOrigin = (() => {
    try {
      return previewUrl ? new URL(previewUrl).origin : '';
    } catch {
      return '';
    }
  })();
  const previewHost = (() => {
    try {
      return previewUrl ? new URL(previewUrl).host : '';
    } catch {
      return '';
    }
  })();
  const currentUrl = previewOrigin ? previewOrigin + path : '';
  const showIframe = !!previewUrl && (readiness?.preview_ready ?? !isProvisioning);
  // StatusDot mapuje běžné stavy — ready ('applied'/'ok'/'running') → zelená;
  // provisioning ('pending'/'partial') → žlutá; error → červená.
  const dotStatus = isReady
    ? 'running'
    : readinessFailed
    ? 'error'
    : isProvisioning
    ? 'pending'
    : status;
  const lastPublishCall = data?.last_publish_event ?? null;
  const devGit = data?.project_status?.dev ?? null;
  const workspaceHead = devGit?.head ?? null;
  const workspaceShort = devGit?.short_head
    ?? (workspaceHead ? workspaceHead.slice(0, 8) : null);
  const remoteHead = devGit?.remote_head ?? null;
  const onOrigin = workspaceHead && remoteHead ? workspaceHead === remoteHead : null;
  // Publikace čte živý git workspace, ne sloupec last_commit_sha (ten plní jen úspěšný codable.commit).
  const lastCommitSha = workspaceHead;
  const lastCommitShort = workspaceShort;
  const devUncommitted = devGit?.dirty === true;
  const devChanged = devGit?.changed_files ?? 0;
  const devDetail = workspaceShort
    ? (devUncommitted
      ? t('codable.wsDevHeadDirty', '{{sha}} · {{count}} nezapsaných', {
        sha: workspaceShort,
        count: devChanged,
      })
      : t('codable.wsDevHeadClean', '{{sha}} · čistý', { sha: workspaceShort }))
    : null;
  const lastProdSha = summaryString(lastPublishCall?.result_summary, [
    'deployed_sha',
    'commit_sha',
    'sha',
    'short_sha',
  ]);
  const updatedAfterPublish = Boolean(
    p.updated_at &&
      lastPublishCall?.created_at &&
      new Date(p.updated_at).getTime() >
        new Date(lastPublishCall.created_at).getTime(),
  );
  const prodShaDiffers = Boolean(
    lastCommitSha && lastProdSha && !sameGitRef(lastCommitSha, lastProdSha),
  );
  const prodUnknownButOlder = Boolean(
    lastCommitSha && !lastProdSha && lastPublishCall && updatedAfterPublish,
  );
  const publishHasNewBuild = Boolean(
    data?.fe_split === true && publishActual && lastCommitSha &&
      (prodShaDiffers || prodUnknownButOlder || lastPublishCall?.ok === false),
  );
  const topologyValue = (data?.topology?.target ?? data?.topology?.mode ??
    (data?.fe_split ? 'normal' : publishActual ? 'simple' : 'beta')) as TopologyTarget;
  const topologyActual = data?.topology?.actual ?? null;
  const carouselMode = (topologyActual && topologyActual !== 'none'
    ? topologyActual
    : topologyValue) as 'beta' | 'simple' | 'normal' | 'full_split';
  const topologyDrift = data?.topology?.drift === true;
  const topologyBadgeVariant = topologyDrift
    ? 'yellow'
    : topologyValue === 'beta'
    ? 'purple'
    : topologyValue === 'simple'
    ? 'gray'
    : 'blue';
  const shownProjectId = visibleProjectId(
    data?.orchestrator_project_id,
    data?.id ?? p.id,
  );
  const projectStatus = data?.project_status ?? null;

  return (
    // Root musí clipnout: parent AppLayout main má `overflow-y-auto`, takže bez
    // explicit overflow-hidden by chat pane roll přes 100vh a scrolloval by se
    // celý dashboard, ne interní scroll v chatu. Filip 2026-07-10: „chat se
    // prodlužuje, náhledové okno nezůstává pevné."
    <div className='h-[calc(100vh-4rem)] flex flex-col overflow-hidden'>
      {/* Toolbar */}
      <div className='flex items-center gap-2 px-4 py-2 border-b border-gray-800 bg-gray-950'>
        <div className='flex items-center gap-3 min-w-0 flex-1'>
          <BackButton to='/modules/codable' />
          <img src='/codable/logo.jpg' alt='' className='w-6 h-6 rounded' />
          <div className='min-w-0'>
            <div className='font-bold text-white text-sm truncate'>
              {p.name}
            </div>
            <div className='text-xs text-gray-500 font-mono truncate'>
              {p.slug}
              {
                /* Identita projektu musí být vidět i po otevření — bez ní se na projekt
                  nedá odkázat a zbývá jméno, které se recykluje (Filip 2026-08-13).
                  `select-all` = dá se rovnou zkopírovat. */
              }
              {shownProjectId && (
                <>
                  <span className='mx-1 text-gray-700'>·</span>
                  <span
                    className='select-all text-gray-400'
                    title={t('codable.prjIdTitle', 'Project ID: {{id}}', {
                      id: shownProjectId,
                    })}
                  >
                    {shownProjectId}
                  </span>
                </>
              )}
            </div>
          </div>
          <StatusDot
            status={dotStatus}
            label={status}
            withLabel
            className='ml-2 flex-shrink-0'
          />
          <div
            className='ml-1 flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-800 bg-gray-900/70 px-2 py-1'
            title={t('codable.wsVariantTitle', {
              chosen: topologyLabel(topologyValue),
              actual: topologyLabel(topologyActual),
            })}
          >
            <span className='text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
              {t('codable.wsVariantLabel')}
            </span>
            <Badge variant={topologyBadgeVariant} className='text-[11px]'>
              {topologyLabel(topologyValue)}
            </Badge>
            {topologyActual && topologyActual !== topologyValue && (
              <span
                className={topologyDrift
                  ? 'text-[11px] text-amber-300'
                  : 'text-[11px] text-gray-500'}
              >
                / {topologyLabel(topologyActual)}
              </span>
            )}
            <button
              type='button'
              onClick={() => {
                setTopologyDraft(topologyValue);
                setTopologyProdDomain(
                  data?.topology?.prod_domain ??
                    data?.publish?.domain ??
                    '',
                );
                setFromSettings(false);
                setTopologyInfoOpen(true);
              }}
              className='inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-500 hover:bg-gray-800 hover:text-gray-200'
              title={t('codable.wsExplainVariant')}
              aria-label={t('codable.wsExplainVariant')}
            >
              <Info size={13} />
            </button>
          </div>
        </div>

        {/* Střed — segmentované Preview | Code (v0 pattern) + device toggle */}
        <div className='flex items-center gap-3 flex-shrink-0'>
          <div className='flex items-center rounded-lg border border-gray-800 bg-gray-900/60 p-0.5'>
            {([
              ['preview', t('codable.wsPreview'), Monitor],
              ['code', t('codable.wsCode'), Code2],
            ] as const).map(([v, label, Icon]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  view === v ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-200'
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          {view === 'preview' && (
            <div className='flex items-center gap-0.5'>
              {DEVICE_MODE_DEFS.map(({ mode, icon: DevIcon, labelKey }) => (
                <button
                  key={mode}
                  onClick={() => switchDevice(mode)}
                  title={t(labelKey)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    device === mode
                      ? 'text-blue-400 bg-blue-600/15'
                      : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800/60'
                  }`}
                >
                  <DevIcon size={15} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className='flex items-center gap-2 flex-1 justify-end'>
          {data?.gitlab_web_url && (
            <a href={data.gitlab_web_url} target='_blank' rel='noreferrer'>
              <Button
                size='sm'
                variant='ghost'
                icon={<GitBranch size={15} />}
                title={t('codable.openGitlab', 'Otevřít repo v GitLabu')}
              >
                GitLab
              </Button>
            </a>
          )}
          <Button
            size='sm'
            variant='ghost'
            icon={<RefreshCw size={15} />}
            className='shrink-0 whitespace-nowrap text-gray-400 hover:text-gray-200'
            loading={deployMut.isPending ||
              (lifecycleRunning && lifecycleOperation?.operation_type === 'sync')}
            disabled={lifecycleRunning}
            onClick={() => deployMut.mutate()}
            title={t(
              'codable.deployFromGitHint',
              'Stáhne změny z Gitu do DEV. Vite si je vezme sám — bez rebuildu kontejneru.',
            )}
          >
            {t('codable.deploy', 'Git sync')}
          </Button>
          {lastGitSync && (
            <span
              className={`hidden sm:inline max-w-[14rem] truncate text-[11px] ${
                lastGitSync.ok ? 'text-gray-500' : 'text-red-400'
              }`}
              title={lastGitSync.text}
            >
              {lastGitSync.text}
            </span>
          )}
          <Button
            size='sm'
            variant={publishSyncing ? 'warning' : publishActual ? 'success' : 'primary'}
            icon={<Globe size={15} />}
            onClick={() => setPublishOpen(true)}
            title={t('codable.wsPublication', 'Publikace')}
          >
            {t('codable.wsPublication', 'Publikace')}
            {publishHasNewBuild && lastCommitShort && (
              <span className='ml-1 rounded bg-black/20 px-1 py-0.5 font-mono text-[10px] leading-none'>
                {lastCommitShort}
              </span>
            )}
          </Button>
          <Button
            size='sm'
            variant='ghost'
            icon={<Activity size={15} />}
            onClick={() => {
              setFromSettings(false);
              setActivityOpen(true);
            }}
            title={t('codable.activityTitle')}
          >
            {t('codable.activityShort')}
          </Button>
          <Button
            size='sm'
            variant='ghost'
            icon={<ProjectSecurityIcon slug={p.slug} />}
            onClick={() => setSecurityOpen(true)}
            title={t('codable.wsSecurityScan')}
          />
          {
            /* Zásuvka mezi štítem a ozubeným kolem — IDE napojení s
              průvodcem pro Cursor (Filip 2026-07-25) */
          }
          <Button
            size='sm'
            variant='ghost'
            icon={
              <Plug
                size={15}
                className={ideConnected ? 'text-amber-400' : undefined}
              />
            }
            onClick={() => setConnectOpen(true)}
            title={ideConnected
              ? t('codable.wsIdeConnected', 'IDE připojeno — spravovat')
              : t('codable.wsConnectIde', 'Připojit IDE (Cursor průvodce)')}
          />
          <Button
            size='sm'
            variant='ghost'
            icon={<Settings size={15} />}
            onClick={() => setSettingsOpen(true)}
            title={t('codable.wsSettings')}
          />
          <Button
            size='sm'
            variant='ghost'
            icon={chatOpen ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
            onClick={toggleChat}
            title={chatOpen ? t('codable.wsHideChat') : t('codable.wsShowChat')}
          />
        </div>
      </div>

      {p?.slug && (
        <WorkspaceWorkPanel
          scope={{
            kind: 'project',
            slug: p.slug,
            gitlabPath: data?.gitlab_path ?? null,
            gitlabProjectKey: p.gitlab_project_key,
            instance: data?.workspace_instance ?? null,
          }}
        />
      )}

      {lifecycleOperation && (
        <div
          className={`flex items-center gap-3 border-b px-4 py-2 text-xs ${
            lifecycleOperation.status === 'failed'
              ? 'border-red-500/30 bg-red-950/25 text-red-200'
              : lifecycleOperation.status === 'completed'
              ? 'border-emerald-500/20 bg-emerald-950/15 text-emerald-200'
              : 'border-blue-500/20 bg-blue-950/20 text-blue-200'
          }`}
        >
          {lifecycleRunning && <Loader2 size={14} className='animate-spin' />}
          <span className='font-semibold'>
            {lifecycleOperation.operation_type === 'topology-set'
              ? t('codable.opTopologySet', 'Varianta')
              : lifecycleOperation.operation_type === 'build'
              ? t('codable.opBuild', 'Build')
              : lifecycleOperation.operation_type} · {lifecycleOperation.phase}
          </span>
          {typeof lifecycleOperation.result?.web_url === 'string' && (
            <a
              href={lifecycleOperation.result.web_url}
              target='_blank'
              rel='noreferrer'
              className='underline decoration-dotted hover:text-white'
            >
              {t('codable.buildPipeline', 'pipeline')}
            </a>
          )}
          <div className='h-1.5 min-w-24 flex-1 overflow-hidden rounded-full bg-gray-900'>
            <div
              className={lifecycleOperation.status === 'failed'
                ? 'h-full bg-red-500'
                : 'h-full bg-blue-500'}
              style={{ width: `${Math.max(0, Math.min(100, lifecycleOperation.progress))}%` }}
            />
          </div>
          <span className='font-mono'>{lifecycleOperation.progress}%</span>
          {lifecycleOperation.error && (
            <span className='max-w-md truncate text-red-300' title={lifecycleOperation.error}>
              {lifecycleOperation.error}
            </span>
          )}
          {lifecycleOperation.status === 'failed' && (
            <Button
              size='sm'
              variant='secondary'
              loading={lifecycleRetryMut.isPending}
              onClick={() => lifecycleRetryMut.mutate()}
            >
              {t('codable.retry', 'Zkusit znovu')}
            </Button>
          )}
          {(lifecycleOperation.status === 'completed' ||
            lifecycleOperation.status === 'failed') && (
            <button
              type='button'
              className='rounded p-0.5 text-gray-400 hover:text-gray-100'
              onClick={() => setActiveLifecycleId(null)}
              title={t('common.close', 'Zavřít')}
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {
        /* Split panel — preview + volitelný chat dock (Filip 2026-07-08:
          'codable chat na straně jako skrývatelný, ale s pevným místem'). */
      }
      <div
        className={`flex-1 min-h-0 overflow-hidden grid grid-rows-[minmax(0,1fr)] ${
          chatOpen ? 'grid-cols-[minmax(0,1fr)_500px]' : 'grid-cols-1'
        }`}
      >
        {view === 'code'
          ? <CodePanel slug={p.slug} />
          : (
            <div className='flex flex-col min-h-0 min-w-0'>
              {/* Path bar — adresa je klikací (celý náhled v externím prohlížeči) */}
              {previewUrl && (
                <div className='flex items-center gap-2 py-1.5 px-3 border-b border-gray-800 bg-gray-950/80'>
                  <Globe size={12} className='text-gray-600 flex-shrink-0' />
                  <a
                    href={currentUrl || previewUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={t(
                      'codable.wsOpenFullPreview',
                      'Otevřít náhled v novém okně prohlížeče',
                    )}
                    className='text-blue-400 hover:text-blue-300 text-xs font-mono flex-shrink-0 underline-offset-2 hover:underline max-w-[40%] truncate'
                  >
                    {previewHost}
                  </a>
                  <Input
                    size='sm'
                    value={pathInput}
                    onChange={(e) => setPathInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitPath();
                    }}
                    className='font-mono flex-1 min-w-0'
                    placeholder='/'
                    spellCheck={false}
                    title={t(
                      'codable.wsPathHint',
                      'Cesta v náhledu (Enter = přejít v iframe)',
                    )}
                  />
                  <button
                    type='button'
                    onClick={toggleInspect}
                    title={t('codable.wsSelectElement')}
                    className={`p-1.5 rounded-lg flex-shrink-0 transition-colors ${
                      inspect
                        ? 'text-blue-400 bg-blue-600/15'
                        : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800/60'
                    }`}
                  >
                    <MousePointerClick size={13} />
                  </button>
                  <button
                    type='button'
                    onClick={() => setIframeKey((k) => k + 1)}
                    title={t('codable.wsReloadPreview')}
                    className='p-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-gray-800/60 flex-shrink-0'
                  >
                    <RefreshCw size={13} />
                  </button>
                  <a
                    href={currentUrl || previewUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={t('codable.wsOpenTab')}
                    className='p-1.5 rounded-lg text-blue-400/90 hover:text-blue-300 hover:bg-blue-600/15 flex-shrink-0'
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>
              )}

              {/* Vybraný element — chip pod path barem (jde do kontextu agenta) */}
              {selection && (
                <div className='flex items-center gap-2 px-3 py-1.5 border-b border-gray-800 bg-blue-950/10'>
                  <MousePointerClick
                    size={12}
                    className='text-blue-400 flex-shrink-0'
                  />
                  <code className='text-[11px] font-mono text-blue-300 truncate flex-1'>
                    {selection.selector}
                  </code>
                  {selection.text && (
                    <span className='text-[11px] text-gray-500 truncate max-w-[200px]'>
                      „{selection.text}"
                    </span>
                  )}
                  <button
                    onClick={clearSelection}
                    className='text-gray-600 hover:text-gray-300 flex-shrink-0'
                    title={t('codable.wsCancelSelect')}
                  >
                    <X size={13} />
                  </button>
                </div>
              )}

              {/* Preview plocha — dot pattern podklad, device-width wrapper */}
              <div className='flex-1 min-h-0 bg-gray-950 bg-[radial-gradient(circle,#1f2937_1px,transparent_1px)] [background-size:16px_16px] flex flex-col overflow-hidden'>
                {previewRebuilding
                  ? (
                    <div className='flex-1 flex items-center justify-center p-6'>
                      <LoadingStatus
                        variant='block'
                        tone='working'
                        title={t('codable.wsRebuildingTitle', 'Náhled se přegenerovává')}
                        status={lifecycleOperation
                          ? `${lifecycleOperation.operation_type} · ${lifecycleOperation.phase}`
                          : t(
                            'codable.wsRebuildingHint',
                            'Nový build se teprve skládá. 502 teď není chyba — počkej, než se náhled položí.',
                          )}
                        progress={lifecycleOperation
                          ? Math.max(0, Math.min(100, lifecycleOperation.progress))
                          : null}
                      />
                    </div>
                  )
                  : showIframe
                  ? (
                    <div
                      className={`mx-auto h-full w-full transition-[max-width] duration-300 ${
                        DEVICE_WIDTH[device]
                      }`}
                    >
                      <iframe
                        ref={iframeRef}
                        key={iframeKey}
                        src={previewSrcWithBust(currentUrl, iframeKey)}
                        className='w-full h-full border-0 bg-white'
                        title={`Preview ${p.slug}`}
                        sandbox='allow-forms allow-scripts allow-same-origin allow-popups allow-downloads allow-modals'
                        onLoad={onIframeLoad}
                      />
                    </div>
                  )
                  : (
                    <div className='flex-1 flex items-center justify-center p-6'>
                      <div className='bg-gray-900/90 border border-gray-800 rounded-2xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl'>
                        {
                          /* Tři stavy, ne dva. Panel se zobrazuje vždy, když není
                            preview URL — a to je i případ HOTOVÉHO projektu, kterému
                            je publikace preview záměrně vypnutá. Do 2026-08-12 měl
                            nadpis jen „chyba / připravuji", takže dokončený projekt
                            se tvářil, že se pořád staví (Filip to viděl na
                            id-mandate-check: readiness ready, všechny checky zelené,
                            a nad tím „Připravuji prostředí…"). */
                        }
                        {readinessFailed
                          ? <AlertTriangle size={28} className='mx-auto text-red-400' />
                          : isReady
                          ? <CheckCircle2 size={28} className='mx-auto text-emerald-400' />
                          : (
                            <Loader2
                              size={28}
                              className='mx-auto animate-spin text-blue-400'
                            />
                          )}
                        <h2 className='font-bold text-white text-base'>
                          {readinessFailed
                            ? t('codable.wsEnvironmentError', 'Prostředí není připravené')
                            : isReady
                            ? t('codable.wsReadyNoPreview', 'Projekt je připravený')
                            : t('codable.wsPreparingEnv')}
                        </h2>
                        <div className='flex justify-center'>
                          <StatusDot
                            status={dotStatus}
                            label={readiness?.phase ?? status}
                            withLabel
                          />
                        </div>
                        <p className='text-xs text-gray-500'>
                          {readiness?.detail ?? t('codable.wsPreparingEnvHint')}
                        </p>
                        {readiness?.checks?.length
                          ? (
                            <div className='space-y-1.5 pt-2 text-left'>
                              {readiness.checks.map((check) => (
                                <div
                                  key={check.key}
                                  className='flex items-start gap-2 rounded border border-gray-800 bg-gray-950/60 px-2 py-1.5 text-[11px]'
                                >
                                  <span
                                    className={check.ok ? 'text-emerald-400' : 'text-amber-400'}
                                  >
                                    {check.ok ? '✓' : '•'}
                                  </span>
                                  <span className='text-gray-400'>{check.detail}</span>
                                </div>
                              ))}
                            </div>
                          )
                          : (
                            <div className='space-y-2 pt-2' aria-hidden='true'>
                              <div className='h-2 rounded bg-gray-800 animate-pulse' />
                              <div className='h-2 rounded bg-gray-800 animate-pulse w-3/4 mx-auto' />
                              <div className='h-2 rounded bg-gray-800 animate-pulse w-1/2 mx-auto' />
                            </div>
                          )}
                        <Button
                          size='sm'
                          variant='secondary'
                          onClick={() => void refetch()}
                        >
                          {t('common.refresh', 'Ověřit znovu')}
                        </Button>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}
        {chatOpen && (
          <CodableChatPane
            projectId={p.id}
            orchestratorProjectId={data?.orchestrator_project_id ?? null}
            slug={p.slug}
            repo={p.gitlab_project_key ?? undefined}
            seedPrompt={p.description ?? undefined}
            knowledge={(p as { knowledge?: string | null }).knowledge ?? null}
            previewUrl={previewUrl || null}
            prodPreviewUrl={data?.production?.preview_url ?? null}
            gitlabUrl={data?.gitlab_web_url ?? null}
            publish={data?.publish ?? null}
            envStatus={status}
            workspaceInstance={data?.workspace_instance ?? null}
            prodInstance={data?.prod_instance ?? null}
            feSplit={data?.fe_split ?? false}
            topology={data?.topology ?? null}
            projectStatus={projectStatus}
            lastCommitSha={p.last_commit_sha ?? null}
            lastPublishEvent={data?.last_publish_event ?? null}
            onOpenConnectIde={() => setConnectIdeOpen(true)}
            onOpenLlmModels={() => setLlmModelsOpen(true)}
            onDeployed={() => {
              setIframeKey((k) => k + 1);
              refreshCodableWorkspace(qc, { slug: p.slug });
            }}
          />
        )}
      </div>

      {/* Connect IDE modal — MCP token management pro externí klienty */}
      {connectIdeOpen && (
        <ConnectIdeModal
          slug={p.slug}
          orchestratorProjectId={data?.orchestrator_project_id ?? null}
          onClose={() => setConnectIdeOpen(false)}
          onBack={fromSettings
            ? () => {
              setConnectIdeOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {/* Moje LLM modely — BYOK profily volajiciho uzivatele (entity-scoped) */}
      {llmModelsOpen && (
        <ProjectLlmSettingsModal
          slug={p.slug}
          onClose={() => setLlmModelsOpen(false)}
          onBack={fromSettings
            ? () => {
              setLlmModelsOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {/* Aktivita — MCP log + git timeline + presence (hlavní vstup) */}
      {activityOpen && (
        <ActivityModal
          slug={p.slug}
          onClose={() => setActivityOpen(false)}
          onBack={fromSettings
            ? () => {
              setActivityOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {/* Historie verzí — list commitů + revert (codable.history) */}
      {historyOpen && (
        <HistoryModal
          slug={p.slug}
          onClose={() => setHistoryOpen(false)}
          onBack={fromSettings
            ? () => {
              setHistoryOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {/* Znalosti projektu — persistentní instrukce pro asistenta (codable.knowledge) */}
      {knowledgeOpen && (
        <KnowledgeModal
          slug={p.slug}
          initialTab={knowledgeTab}
          onClose={() => setKnowledgeOpen(false)}
          onBack={fromSettings
            ? () => {
              setKnowledgeOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {projectLogOpen && (
        <ProjectLogModal
          slug={p.slug}
          onClose={() => setProjectLogOpen(false)}
          onBack={fromSettings
            ? () => {
              setProjectLogOpen(false);
              backToSettings();
            }
            : undefined}
        />
      )}

      {connectOpen && (
        <McpConnectModal
          onClose={() => setConnectOpen(false)}
          projectSlug={p.slug}
          orchestratorProjectId={data?.orchestrator_project_id ?? null}
        />
      )}
      {securityOpen && (
        <ProjectSecurityModal
          slug={p.slug}
          envName={p.env_name}
          onClose={() => setSecurityOpen(false)}
        />
      )}

      {topologyInfoOpen && (
        <Modal
          open
          onClose={() => !topologyBusy && setTopologyInfoOpen(false)}
          onBack={fromSettings && !topologyBusy
            ? () => {
              setTopologyInfoOpen(false);
              backToSettings();
            }
            : undefined}
          backLabel={t('codable.wsSettings')}
          title={t('codable.wsTopologyTitle')}
          size='md'
          footer={
            <div className='flex flex-wrap gap-2 justify-end'>
              <Button
                variant='secondary'
                disabled={topologyBusy}
                onClick={() => setTopologyInfoOpen(false)}
              >
                {t('codable.close')}
              </Button>
              {data?.orchestrator_project_id && (
                <>
                  <Button
                    variant='secondary'
                    loading={topologyBusy}
                    disabled={topologyBusy}
                    onClick={() => void applyTopologyChange({ apply: false })}
                  >
                    {t('codable.wsTopologySaveChoice', 'Save choice')}
                  </Button>
                  <Button
                    variant='primary'
                    loading={topologyBusy}
                    disabled={topologyBusy}
                    onClick={() => void applyTopologyChange({ apply: true })}
                  >
                    {t('codable.wsTopologySaveApply', 'Save and apply')}
                  </Button>
                </>
              )}
            </div>
          }
        >
          <div className='space-y-3 text-sm text-gray-300'>
            <p>{t('codable.wsTopologyIntro')}</p>
            <div className='flex flex-wrap gap-2 text-xs text-gray-500'>
              <span>
                {t('codable.wsTopologyChosen', 'Chosen')}:{' '}
                <strong className='text-gray-200'>
                  {topologyLabel(topologyValue)}
                </strong>
              </span>
              {topologyActual && (
                <span>
                  {t('codable.wsTopologyActual', 'Verified')}:{' '}
                  <strong className='text-gray-200'>
                    {topologyLabel(topologyActual)}
                  </strong>
                </span>
              )}
            </div>
            {topologyDrift && (
              <div className='rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200'>
                {t('codable.wsTopologyDrift')}
              </div>
            )}
            {!data?.orchestrator_project_id
              ? (
                <div className='text-xs text-amber-300'>
                  {t(
                    'codable.wsTopologyNoOrch',
                    'Project is not bound to orchestrator — topology cannot be changed here.',
                  )}
                </div>
              )
              : (
                <div className='space-y-3'>
                  <div className='grid gap-2'>
                    {(
                      [
                        ['beta', 'codable.wsTopologyBeta', 'text-purple-300'],
                        ['simple', 'codable.wsTopologySimple', 'text-gray-200'],
                        ['normal', 'codable.wsTopologyNormal', 'text-blue-300'],
                        [
                          'full_split',
                          'codable.wsTopologyFullSplit',
                          'text-blue-300',
                        ],
                      ] as const
                    ).map(([id, labelKey, colorCls]) => {
                      const approved = approvedTargets.includes(id);
                      return (
                      <label
                        key={id}
                        className={`flex items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                          !approved
                            ? 'border-gray-800/60 opacity-50 cursor-not-allowed'
                            : topologyDraft === id
                            ? 'border-plex/50 bg-plex/5 cursor-pointer'
                            : 'border-gray-800 hover:border-gray-700 cursor-pointer'
                        }`}
                      >
                        <input
                          type='radio'
                          name='topology-target'
                          className='mt-1'
                          checked={topologyDraft === id}
                          onChange={() => setTopologyDraft(id)}
                          disabled={topologyBusy || !approved}
                        />
                        <span className='text-xs min-w-0'>
                          <span className={`font-semibold ${colorCls}`}>
                            {topologyLabel(id)}
                          </span>
                          {!approved && (
                            <span className='ml-2 text-[10px] uppercase tracking-wide text-gray-500'>
                              {t('codable.doneVariantLocked', 'Neschváleno')}
                            </span>
                          )}
                          {' — '}
                          {t(labelKey)
                            .replace(/^Beta\s*[—-]\s*/i, '')
                            .replace(/^Simple\s*[—-]\s*/i, '')
                            .replace(/^Normal\s*[—-]\s*/i, '')
                            .replace(/^Full Split\s*[—-]\s*/i, '')}
                        </span>
                      </label>
                      );
                    })}
                  </div>
                  {topologyDraft !== 'beta' && (
                    <label className='block space-y-1'>
                      <span className='text-[11px] text-gray-500'>
                        {t(
                          'codable.wsTopologyProdDomain',
                          'PROD domain (required for Simple / Normal / Full Split)',
                        )}
                      </span>
                      <Input
                        value={topologyProdDomain}
                        onChange={(e) => setTopologyProdDomain(e.target.value)}
                        placeholder='app.example.com'
                        disabled={topologyBusy}
                        className='font-mono text-xs'
                      />
                    </label>
                  )}
                  <p className='text-[11px] text-gray-600'>
                    {t(
                      'codable.wsTopologyApplyHint',
                      'Save choice stores the target. Save and apply also provisions instances/domains (additive; does not destroy running services).',
                    )}
                  </p>
                </div>
              )}
          </div>
        </Modal>
      )}

      {settingsOpen && (
        <ProjectSettingsModal
          name={p.name}
          slug={p.slug}
          description={p.description}
          previewUrl={previewUrl}
          prodUrl={data?.production?.ui_url ?? data?.public_url ?? data?.publish?.url ?? null}
          mcpUrl={data?.development?.mcp_url ?? data?.mcp_connection?.url ?? null}
          mcpEnabled={data?.development?.mcp?.enabled === true}
          ssoMode={data?.sso?.mode ?? 'off'}
          appKeyEnabled={data?.hub_app_key_enabled === true}
          oauthEnabled={data?.development?.oauth_enabled === true}
          oauthPlatformEnabled={data?.development?.oauth_platform_enabled === true}
          workspaceService={data?.project_binding?.workspace_service ?? null}
          productionService={data?.project_binding?.production_service ?? null}
          workspaceInstance={data?.workspace_instance ?? null}
          prodInstance={data?.prod_instance ?? null}
          services={data?.services ?? null}
          previewOn={data?.development?.ui_desired === true}
          previewActual={data?.development?.ui_actual === true}
          slotPreviewUrl={data?.production?.preview_url ?? null}
          publishOn={publishDesired}
          publishActual={publishActual}
          publishSyncing={publishSyncing}
          topologyLabel={topologyLabel(topologyValue)}
          gitlabUrl={data?.gitlab_web_url ?? null}
          gitlabPath={data?.gitlab_path ?? null}
          projectId={data?.orchestrator_project_id ?? null}
          backend={data?.backend ?? null}
          source={data?.project?.source ?? 'native'}
          origin={data?.project?.origin ?? null}
          envName={data?.project?.env_name ?? null}
          orchestratorProjectId={data?.orchestrator_project_id ?? null}
          enabling={enableBackendMut.isPending}
          onEnableBackend={() => enableBackendMut.mutate()}
          onOpenTopology={() => {
            setTopologyDraft(topologyValue);
            setTopologyProdDomain(
              data?.topology?.prod_domain ?? data?.publish?.domain ?? '',
            );
            openFromSettings(() => setTopologyInfoOpen(true));
          }}
          onOpenKnowledge={(tab) => {
            setKnowledgeTab(tab ?? 'instructions');
            openFromSettings(() => setKnowledgeOpen(true));
          }}
          onOpenActivity={() => openFromSettings(() => setActivityOpen(true))}
          onOpenHistory={() => openFromSettings(() => setHistoryOpen(true))}
          onOpenProjectLog={() => openFromSettings(() => setProjectLogOpen(true))}
          onOpenIde={() => openFromSettings(() => setConnectIdeOpen(true))}
          onOpenLlmModels={() => openFromSettings(() => setLlmModelsOpen(true))}
          onDeployed={() => {
            setIframeKey((k) => k + 1);
            refreshCodableWorkspace(qc, { slug: p.slug });
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {/* Publish modal — project-qualified apphost owner flow. */}
      {publishOpen && (
        <div className='fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4'>
          <div className='bg-gray-900 border border-gray-800 rounded-lg p-5 max-w-4xl w-full space-y-3 max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between'>
              <h3 className='font-bold text-white text-lg'>
                {t('codable.wsPublishProject')}
              </h3>
              <button
                onClick={() => setPublishOpen(false)}
                className='text-gray-500 hover:text-gray-200'
              >
                <X size={18} />
              </button>
            </div>
            {(data?.project_binding?.project_id && data.project_binding.production_service) ||
                data?.prod_instance || data?.workspace_instance
              ? (
                <ReleaseCarousel
                  tool={data?.project_binding?.project_id &&
                      data.project_binding.production_service
                    ? `${data.project_binding.project_id}.apphost.release_switch`
                    : undefined}
                  args={data?.project_binding?.project_id &&
                      data.project_binding.production_service
                    ? { service: data.project_binding.production_service }
                    : { name: data.prod_instance || data.workspace_instance }}
                  topology={
                    carouselMode === 'simple' || carouselMode === 'normal' ||
                      carouselMode === 'full_split'
                      ? carouselMode
                      : 'beta'
                  }
                  liveUrl={
                    data?.production?.ui_url
                    ?? data?.publish?.url
                    ?? (data?.publish?.domain ? `https://${data.publish.domain}` : null)
                  }
                  previewUrl={data?.production?.preview_url ?? null}
                  publicHead={{
                    url: data?.production?.ui_url
                      ?? data?.publish?.url
                      ?? (data?.publish?.domain ? `https://${data.publish.domain}` : null),
                    domain: data?.publish?.domain ?? data?.topology?.prod_domain ?? null,
                    internal: data?.publish?.is_internal_domain === true,
                    apexWarn: data?.publish?.is_bare_apex_without_alt === true,
                    hidePending: false,
                    onChangeDomain: async (domain: string) => {
                      const prj = data?.orchestrator_project_id;
                      if (!prj) throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
                      const target = carouselMode === 'simple' || carouselMode === 'normal' ||
                          carouselMode === 'full_split'
                        ? carouselMode
                        : 'normal';
                      const r = await api.mcp<{
                        ok?: boolean;
                        error?: string;
                        operation_id?: string;
                      }>(
                        `${prj}.orchestrator.topology-set`,
                        {
                          target,
                          apply: true,
                          confirm: true,
                          prod_domain: domain,
                        },
                      );
                      if (r.ok === false) throw new Error(r.error || 'topology-set');
                      if (r.operation_id) {
                        setHandledLifecycleId(null);
                        setActiveLifecycleId(r.operation_id);
                        return;
                      }
                      refreshCodableWorkspace(qc, { slug: p.slug, preview: false });
                    },
                    onHide: data?.project_binding?.project_id &&
                        data.project_binding.production_service
                      ? () => {
                        void api.mcp(
                          `${data.project_binding!.project_id}.apphost.unpublish`,
                          { service: data.project_binding!.production_service },
                        ).then(() =>
                          refreshCodableWorkspace(qc, { slug: p.slug, preview: false })
                        );
                      }
                      : undefined,
                  }}
                  path={{
                    devUrl: previewUrl || null,
                    gitlabUrl: data?.gitlab_web_url ?? null,
                    gitlabPath: data?.gitlab_path ?? null,
                    sha: lastCommitShort,
                    devDetail,
                    onPull: () => deployMut.mutate(),
                    pullPending: deployMut.isPending ||
                      (lifecycleRunning && lifecycleOperation?.operation_type === 'sync'),
                    onPush: async () => {
                      const msg = await themedPrompt({
                        title: t('releaseCarousel.pushGitlab', 'Nahrát DEV do GitLabu'),
                        label: t('workspaceWork.commitLabel', 'Zpráva commitu'),
                        confirmLabel: t('workspaceWork.commitLink', 'commit'),
                      });
                      if (!msg?.trim()) return;
                      await api.mcp('codable.commit', {
                        slug: p.slug,
                        message: msg.trim(),
                        confirm: true,
                      });
                      refreshCodableWorkspace(qc, { slug: p.slug, preview: false });
                    },
                    onFeed: (_sha: string) => {
                      promoteMut.mutate();
                    },
                    feedPending: promoteMut.isPending ||
                      (lifecycleRunning && lifecycleOperation?.operation_type === 'promote'),
                    feedDisabled: !data?.orchestrator_project_id || promoteMut.isPending ||
                      lifecycleRunning,
                  }}
                  between={publishActual && carouselMode !== 'beta'
                    ? (
                      <div className='rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2 space-y-2'>
                        <AsyncStatusToggle
                          value={data?.publish?.holding === true}
                          actual={data?.publish?.holding === true}
                          pending={holdingMut.isPending}
                          label={
                            <span className='flex items-center gap-2 text-sm'>
                              <AlertTriangle size={13} />
                              {t('codable.wsHolding', 'Construction page')}
                            </span>
                          }
                          renderIdleBadge={(on) =>
                            on
                              ? (
                                <span className='text-xs text-amber-400'>
                                  {data?.publish?.holding_source === 'custom'
                                    ? t('codable.wsHoldingCustom', 'vlastní z DEV')
                                    : data?.publish?.holding_mode === 'updating'
                                    ? t('codable.wsHoldingUpdating', 'probíhají změny')
                                    : t('codable.wsHoldingPreparing', 'připravuje se')}
                                </span>
                              )
                              : (
                                <span className='text-xs text-muted-foreground'>
                                  {t('codable.wsHoldingOff', 'veřejnost vidí ostrou')}
                                </span>
                              )}
                          onToggle={(enabled) =>
                            holdingMut.mutateAsync({
                              enabled,
                              source: data?.publish?.holding_source === 'custom'
                                ? 'custom'
                                : 'universal',
                              mode: data?.publish?.holding_mode === 'updating'
                                ? 'updating'
                                : 'preparing',
                            })}
                        />
                        {data?.publish?.holding !== true && (
                          <p className='text-[11px] leading-snug text-gray-500'>
                            {carouselMode === 'simple'
                              ? t(
                                'codable.wsHoldingOffSimple',
                                'Dočasná stránka je vypnutá. Veřejná adresa i DEV jedou na stejné FE — obě ukazují ostrou.',
                              )
                              : t(
                                'codable.wsHoldingOffSplit',
                                'Dočasná stránka je vypnutá. Internet jde rovnou na ostrou. Zapni ji, když má veřejnost vidět jinou stránku než ostrou.',
                              )}
                          </p>
                        )}
                        {data?.publish?.holding === true && (
                        <div className='flex gap-2'>
                          {([
                            ['universal', t('codable.wsHoldingUniversal', 'ACM standard')],
                            ['custom', t('codable.wsHoldingCustomBtn', 'Vlastní z DEV')],
                          ] as const).map(([src, label]) => (
                            <button
                              key={src}
                              type='button'
                              disabled={holdingMut.isPending || !publishActual}
                              onClick={() =>
                                holdingMut.mutate({
                                  enabled: true,
                                  source: src,
                                  mode: 'preparing',
                                })}
                              className={`flex-1 rounded-md border px-2 py-1 text-[11px] ${
                                data?.publish?.holding === true &&
                                  (data.publish.holding_source ?? 'universal') === src
                                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                                  : 'border-gray-800 text-gray-500 hover:text-gray-300'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        )}
                        {data?.publish?.holding === true &&
                          data?.publish?.holding_source !== 'custom' && (
                          <div className='flex gap-2'>
                            {(['preparing', 'updating'] as const).map((mode) => (
                              <button
                                key={mode}
                                type='button'
                                disabled={holdingMut.isPending || !publishActual}
                                onClick={() =>
                                  holdingMut.mutate({
                                    enabled: true,
                                    source: 'universal',
                                    mode,
                                  })}
                                className={`flex-1 rounded-md border px-2 py-1 text-[11px] ${
                                  data?.publish?.holding === true &&
                                    data.publish.holding_mode === mode
                                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                                    : 'border-gray-800 text-gray-500 hover:text-gray-300'
                                }`}
                              >
                                {mode === 'updating'
                                  ? t('codable.wsHoldingUpdating', 'Probíhají změny')
                                  : t('codable.wsHoldingPreparing', 'Projekt se připravuje')}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                    : undefined}
                />
              )
              : (
                <p className='text-xs text-gray-600'>
                  {t('codable.wsProdUrlPending', 'PROD URL zatím není publikovaná')}
                </p>
              )}
            {!publishActual && (
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  setPublishOpen(false);
                  setFromSettings(false);
                  setTopologyInfoOpen(true);
                }}
              >
                {t('codable.wsProdDomainSet', 'Nastavit produkční doménu (Topologie)')}
              </Button>
            )}
            <div className='rounded-lg border border-gray-800 bg-gray-950/30 p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs'>
              <div>
                <div className='text-[10px] uppercase tracking-wide text-gray-600 font-semibold mb-1'>
                  {t('codable.wsWorkVariant', 'Varianta práce')}
                </div>
                <div className='flex items-center gap-1.5 text-gray-300'>
                  <span>{topologyLabel(topologyValue)}</span>
                  {topologyActual && (
                    <span
                      className={topologyDrift ? 'text-amber-300' : 'text-gray-500'}
                    >
                      ({topologyLabel(topologyActual)})
                    </span>
                  )}
                  {splitPublished && (
                    <span
                      className='inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-700 text-gray-500 hover:border-amber-500/40 hover:text-amber-300'
                      title={t(
                        'codable.wsSplitPublishHint',
                        'U split projektu publikace určuje PROD URL. Nový commit z DEV workspace se na PROD dostane až přes Publikovat.',
                      )}
                      aria-label={t('codable.wsPublishHelpAria', 'Nápověda k publish topologii')}
                    >
                      <Info size={10} />
                    </span>
                  )}
                </div>
                <div className='mt-1 text-[11px] text-gray-500'>
                  {data?.fe_split
                    ? t('codable.wsSplitFe', 'DEV/PROD FE oddělené')
                    : t('codable.wsOneFe', 'jedna FE instance')}
                </div>
              </div>
              <div>
                <div className='text-[10px] uppercase tracking-wide text-gray-600 font-semibold mb-1'>
                  {t('codable.wsLastCommit', 'Poslední commit')}
                </div>
                <LastCommitMeta
                  gitlabPath={data?.gitlab_path ?? null}
                  sha={workspaceHead}
                  onOrigin={onOrigin}
                  dirty={devUncommitted}
                  changedFiles={devChanged}
                />
              </div>
              <div>
                <div className='text-[10px] uppercase tracking-wide text-gray-600 font-semibold mb-1'>
                  {t('codable.wsLastPublish', 'Poslední publish/promote')}
                </div>
                <div
                  className={lastPublishCall?.ok === false ? 'text-red-300' : 'text-gray-300'}
                >
                  {lastPublishCall
                    ? `${fmtDateTime(lastPublishCall.created_at)} · ${
                      lastPublishCall.actor_entity_id ??
                        lastPublishCall.actor_key_preview ??
                        t('codable.wsUnknownActor', 'neznámý actor')
                    }`
                    : t('codable.wsNoRecord', 'bez záznamu')}
                </div>
              </div>
            </div>
            {data?.project_binding?.project_id &&
                data.project_binding.production_service
              ? (
                <PublishPanel
                  module='apphost'
                  instance={data?.prod_instance ??
                    `${p.env_name ?? p.slug}-frontend`}
                  port={0}
                  projectId={data.project_binding.project_id}
                  serviceKey={data.project_binding.production_service}
                  // Deklarovaná PROD doména z topologie se předvyplní — přepínač
                  // „Veřejná URL" pak zapíná přesně ji, ne prázdné pole (Filip
                  // 2026-09-05: „nevím, kde je na ní switch").
                  currentDomain={data?.publish?.domain ?? data?.topology?.prod_domain ?? null}
                  published={publishActual}
                  syncing={publishSyncing}
                  hideAddress
                  onChanged={() => refreshCodableWorkspace(qc, { slug: p.slug })}
                />
              )
              : (
                <div className='rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-300'>
                  {t(
                    'codable.wsNoProjectBinding',
                    'Projekt nemá ověřenou vazbu na apphost službu; publikace je zablokovaná.',
                  )}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function previewSrcWithBust(url: string, key: number): string {
  if (!url || key <= 0) return url;
  const join = url.includes('?') ? '&' : '?';
  return `${url}${join}_acm=${key}`;
}

function securityTarget(slug: string): string {
  return `codable:${slug}`;
}

function securityPath(slug: string, envName?: string | null): string {
  return `/opt/apphost/${envName ?? slug}-frontend/src`;
}

function severityClass(severity: string): string {
  if (severity === 'high') {
    return 'text-red-400 bg-red-500/10 border-red-500/20';
  }
  if (severity === 'med') {
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  }
  return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
}

function LastCommitMeta({
  gitlabPath,
  sha,
  onOrigin = null,
  dirty = false,
  changedFiles = 0,
}: {
  gitlabPath: string | null;
  sha: string | null;
  onOrigin?: boolean | null;
  dirty?: boolean;
  changedFiles?: number;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['gitlab-commits-tape', gitlabPath],
    enabled: !!gitlabPath,
    queryFn: () =>
      api.mcp<{
        commits?: Array<{
          id: string;
          short_id?: string;
          title?: string;
          author_name?: string;
          authored_date?: string;
          committed_date?: string;
        }>;
      }>('gitlab.commits', {
        action: 'list',
        project: gitlabPath,
        per_page: 20,
      }, { silent: true }),
    staleTime: 30_000,
  });
  const commits = q.data?.commits ?? [];
  const hit = sha
    ? commits.find((c) => c.id.startsWith(sha) || (c.short_id ?? '').startsWith(sha.slice(0, 8)))
    : commits[0];
  if (!sha && !hit) {
    return (
      <div className='font-mono text-gray-300'>
        {t('codable.wsCommitUnknown', 'nezjištěn')}
      </div>
    );
  }
  const short = hit?.short_id || (sha ? sha.slice(0, 8) : '—');
  const when = hit?.authored_date || hit?.committed_date;
  const originLine = onOrigin === true
    ? t('codable.wsCommitOnOrigin', 'na originu')
    : onOrigin === false
    ? t('codable.wsCommitNotOnOrigin', 'není na originu')
    : t('codable.wsCommitOriginUnknown', 'origin neověřen');
  const dirtyLine = dirty
    ? t('codable.wsCommitDirty', '{{count}} nezapsaných', { count: changedFiles })
    : null;
  return (
    <div className='text-gray-300'>
      <div className='font-mono'>{short}</div>
      <div className='mt-0.5 text-[11px] text-gray-500'>
        {[originLine, dirtyLine, hit?.author_name, when ? fmtDateTime(when) : null]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  );
}

function ProjectSecurityIcon({ slug }: { slug: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['codable-security-results', slug],
    queryFn: () =>
      api.mcp<SecurityResultsResp>('codable.security_results', {
        slug,
      }),
    retry: false,
    staleTime: 60_000,
  });
  const summary = data?.filtered_summary;
  const cls = isLoading
    ? 'text-gray-500'
    : data?.scan_incomplete
    ? 'text-amber-400'
    : data?.error || !summary || data.target_in_scan === false
    ? 'text-gray-500'
    : summary.high > 0
    ? 'text-red-400'
    : summary.med > 0
    ? 'text-amber-400'
    : summary.total > 0
    ? 'text-blue-400'
    : 'text-emerald-400';
  return <Shield size={15} className={cls} />;
}

function ProjectSecurityModal(
  { slug, envName, onClose }: {
    slug: string;
    envName?: string | null;
    onClose: () => void;
  },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<SecurityFilter>('all');
  const target = securityTarget(slug);
  const scanPath = securityPath(slug, envName);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['codable-security-results', slug],
    queryFn: () =>
      api.mcp<SecurityResultsResp>('codable.security_results', {
        slug,
      }),
    retry: false,
    staleTime: 30_000,
  });
  const runScan = useMutation({
    mutationFn: () =>
      api.mcp<{
        ok?: boolean;
        scan_id?: string;
        summary?: SecuritySummary;
        error?: string;
        scan_incomplete?: boolean;
      }>(
        'codable.security_scan',
        { slug },
      ),
    onSuccess: (r) => {
      if (r.error) {
        toast.error(r.error);
        if (r.scan_incomplete) {
          qc.invalidateQueries({
            queryKey: ['codable-security-results', slug],
          });
          refetch();
        }
        return;
      }
      toast.success(t('codable.wsSecurityDone'));
      qc.invalidateQueries({ queryKey: ['codable-security-results', slug] });
      refetch();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const findings = data?.findings ?? [];
  const summary = data?.filtered_summary ??
    { high: 0, med: 0, low: 0, total: 0 };
  const filteredFindings = findings
    .filter((f) => severityFilter === 'all' || f.severity === severityFilter)
    .sort((a, b) => {
      const order: Record<string, number> = { high: 0, med: 1, low: 2 };
      return (order[a.severity] ?? 9) - (order[b.severity] ?? 9) ||
        a.file.localeCompare(b.file) ||
        a.line - b.line;
    });
  const summaryCards: Array<{
    filter: SecurityFilter;
    label: string;
    value: number;
    className: string;
    activeClassName: string;
  }> = [
    {
      filter: 'all',
      label: t('codable.wsTotal'),
      value: summary.total,
      className: 'border-gray-800 hover:border-blue-500/30',
      activeClassName: 'border-blue-500/40 bg-blue-500/10',
    },
    {
      filter: 'high',
      label: 'HIGH',
      value: summary.high,
      className: 'border-red-500/20 bg-red-500/5 hover:border-red-500/40',
      activeClassName: 'border-red-500/60 bg-red-500/15',
    },
    {
      filter: 'med',
      label: 'MED',
      value: summary.med,
      className: 'border-amber-500/20 bg-amber-500/5 hover:border-amber-500/40',
      activeClassName: 'border-amber-500/60 bg-amber-500/15',
    },
    {
      filter: 'low',
      label: 'LOW',
      value: summary.low,
      className: 'border-gray-700 bg-gray-800/30 hover:border-gray-500',
      activeClassName: 'border-gray-400 bg-gray-700/40',
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      size='xl'
      title={
        <span className='inline-flex items-center gap-2'>
          <Shield size={17} className='text-blue-400' />
          {t('codable.wsSecurityTitle')}
        </span>
      }
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>
            {t('codable.close')}
          </Button>
          <Button
            variant='primary'
            loading={runScan.isPending}
            icon={<Shield size={14} />}
            onClick={() => runScan.mutate()}
          >
            {t('codable.wsSecurityRun')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='flex flex-wrap items-center gap-2 text-xs'>
          <Badge variant='blue'>{target}</Badge>
          <span className='font-mono text-gray-500'>{scanPath}</span>
          {data?.finished_at && (
            <span className='text-gray-500'>
              {t('codable.wsLastScan', 'poslední scan {{when}}', {
                when: new Date(data.finished_at).toLocaleString(),
              })}
            </span>
          )}
        </div>

        {isLoading
          ? (
            <div className='flex justify-center py-8'>
              <Loader2 className='animate-spin text-gray-600' />
            </div>
          )
          : data?.scan_incomplete
          ? (
            <div className='border border-amber-500/30 rounded-lg p-4 text-sm text-amber-200 bg-amber-500/10'>
              <div className='font-semibold text-amber-100 mb-1'>
                {t('codable.wsScanUnusable', 'Scan není použitelný')}
              </div>
              <div>
                {data.error ?? data.warning ??
                  t(
                    'codable.wsScanNoSources',
                    'Projektový workspace neobsahuje scanovatelné zdrojové soubory.',
                  )}
              </div>
            </div>
          )
          : data?.error || data?.target_in_scan === false
          ? (
            <div className='border border-gray-800 rounded-lg p-4 text-sm text-gray-400'>
              {t(
                'codable.wsScanEmpty',
                'Pro tento projekt zatím nejsou výsledky. Spusť ruční scan.',
              )}
            </div>
          )
          : (
            <>
              <div className='grid grid-cols-4 gap-2'>
                {summaryCards.map((card) => (
                  <button
                    key={card.filter}
                    type='button'
                    onClick={() => setSeverityFilter(card.filter)}
                    className={`text-left border rounded-lg p-3 transition-colors ${
                      severityFilter === card.filter ? card.activeClassName : card.className
                    }`}
                  >
                    <div
                      className={`text-xs ${
                        card.filter === 'high'
                          ? 'text-red-400'
                          : card.filter === 'med'
                          ? 'text-amber-400'
                          : 'text-gray-500'
                      }`}
                    >
                      {card.label}
                    </div>
                    <div className='text-lg font-semibold text-white'>
                      {card.value}
                    </div>
                  </button>
                ))}
              </div>

              {data?.warning && (
                <div className='border border-amber-500/25 rounded-lg p-3 text-xs text-amber-200 bg-amber-500/10'>
                  {data.warning}
                </div>
              )}

              {findings.length === 0
                ? (
                  <div className='border border-emerald-500/20 rounded-lg p-4 text-sm text-emerald-300 bg-emerald-500/5'>
                    {t('codable.wsScanClean', 'Bez nálezů pro tento Codable projekt.')}
                  </div>
                )
                : (
                  <div className='max-h-[420px] overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-800'>
                    {filteredFindings.map((f, i) => (
                      <div key={`${f.file}-${f.line}-${i}`} className='p-3'>
                        <div className='flex items-start gap-2'>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold border shrink-0 ${
                              severityClass(f.severity)
                            }`}
                          >
                            {f.severity.toUpperCase()}
                          </span>
                          <div className='min-w-0 flex-1'>
                            <div className='text-sm text-white'>
                              {f.message}
                            </div>
                            <div className='text-[11px] text-gray-500 font-mono truncate'>
                              {f.file}:{f.line} · {f.category}
                            </div>
                            <pre className='mt-2 text-[10px] text-gray-400 bg-gray-950/70 rounded p-2 overflow-x-auto'>
                              {f.content}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredFindings.length === 0 && (
                      <div className='p-4 text-sm text-gray-500'>
                        {t('codable.wsScanFilterEmpty', 'Žádné nálezy pro vybraný filtr.')}
                      </div>
                    )}
                  </div>
                )}
            </>
          )}
      </div>
    </Modal>
  );
}
